/**
 * External dependencies
 */
import camelCase from 'lodash/camelCase';
import kebabCase from 'lodash/kebabCase';
import mapValues from 'lodash/mapValues';
import uniq from 'lodash/uniq';
import { calculate as getSelectorPriority } from 'specificity';
import type {
	CssEditableProp,
	CssLeafPropName,
	CssProp,
	CssPropName,
	CssShorthandPropName,
	Maybe,
} from '@nab/types';

export function expandProp< T extends CssProp >( prop: T ): ReadonlyArray< T > {
	if ( ! isShorthandProp( prop.name ) ) {
		return [ prop ];
	}

	const el = document.createElement( 'div' );
	el.style[ prop.name ] = prop.value;
	const el2 = document.createElement( 'div' );
	el2.style[ prop.name ] =
		'readonly' !== prop.type ? prop.base?.value ?? '' : '';

	// Keep original background color if it’s a HEX color.
	if ( 'background' === prop.name || 'backgroundColor' === prop.name ) {
		if (
			/^#?([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test( prop.value )
		) {
			return [
				{
					...prop,
					name: 'backgroundColor',
					...( 'readonly' !== prop.type &&
						prop.base && {
							base: {
								...prop.base,
								value: el2.style.backgroundColor,
							},
						} ),
				},
			];
		}
	}

	// Keep original background image if it’s a linear/radial gradient.
	if ( 'background' === prop.name || 'backgroundImage' === prop.name ) {
		if ( /^#?(linear|radial)-gradient\(.+\)$/i.test( prop.value ) ) {
			return [
				{
					...prop,
					name: 'backgroundImage',
					...( 'readonly' !== prop.type &&
						prop.base && {
							base: {
								...prop.base,
								value: el2.style.backgroundColor,
							},
						} ),
				},
			];
		}
	}

	return expandPropName( prop.name ).map(
		( leafName ): T => ( {
			...prop,
			name: leafName,
			value: el.style[ leafName ],
			...( 'readonly' !== prop.type &&
				prop.base && {
					base: {
						...prop.base,
						value: el2.style[ leafName ],
					},
				} ),
		} )
	);
}

export function expandPropName(
	propName: string
): ReadonlyArray< CssLeafPropName > {
	propName = camelCase( propName );
	return isPropName( propName ) ? EXPANSIONS[ propName ] : [];
}

export function getGlobalProps(
	selector: string,
	originalElement: HTMLElement,
	styleTag: HTMLElement | null
): ReadonlyArray< CssProp > {
	const el = originalElement.cloneNode() as HTMLElement;
	originalElement.replaceWith( el );

	const css = styleTag?.innerHTML ?? '';
	if ( styleTag ) {
		styleTag.innerHTML = '';
	}

	const style = getComputedStyle( el );
	const priority = getSelectorPriority( selector );
	const props = Object.keys( TEST_PROP_VALUES )
		.map( ( leafName ) => leafName as CssLeafPropName )
		.map( ( name ): CssEditableProp & { name: CssLeafPropName } => ( {
			type: 'regular',
			name,
			selectors: [ '.nelio-ab-testing--global-prop', selector ],
			value: style[ name ],
			base: undefined,
			ruleIndex: -1,
			priority,
		} ) );

	if ( ! styleTag ) {
		el.replaceWith( originalElement );
		return props;
	}

	const higherPriority = { ...priority, A: priority.A + 1 };
	styleTag.innerHTML = '';

	const previousPointerEvents = el.style.pointerEvents;
	el.style.pointerEvents = 'none';
	const computedStyles = mapValues( TEST_PROPS_CSS, ( _, i ) => {
		const k = i as keyof typeof TEST_PROPS_CSS;
		const cssStyle = TEST_PROPS_CSS[ k ];
		styleTag.innerHTML = `${ selector } { ${ cssStyle } }`;
		void el.offsetWidth;
		const currentStyle = getComputedStyle( el );

		return LEAF_PROP_NAMES.reduce(
			( r, n ) => ( { ...r, [ n ]: currentStyle[ n ] } ),
			{} as unknown as Record< CssLeafPropName, string >
		);
	} );
	el.style.pointerEvents = previousPointerEvents;

	const result = props
		.map( ( p ): CssProp | undefined => {
			const val1 = computedStyles.base[ p.name ];
			const val2 = computedStyles.alternative[ p.name ];
			if ( val1 !== val2 ) {
				return undefined;
			}

			const impVal1 = computedStyles.importantBase[ p.name ];
			const impVal2 = computedStyles.importantAlternative[ p.name ];
			if ( impVal1 !== impVal2 ) {
				return { ...p, priority: higherPriority, type: 'important' };
			}

			return { ...p, priority: higherPriority, type: 'readonly' };
		} )
		.filter( ( p ) => !! p );

	styleTag.innerHTML = css;

	el.replaceWith( originalElement );
	return result.map( fixNumericValues );
}

export const isPropName = ( name: string ): name is CssPropName =>
	( PROP_NAMES as string[] ).includes( name );

export function getPreviewValue( prop: Maybe< CssProp > ): string {
	return getUserValue( prop ) || getInheritedValue( prop ) || '';
}

export function getUserValue( prop: Maybe< CssProp > ): Maybe< string > {
	return 'readonly' === prop?.type ? undefined : prop?.value;
}

export function getInheritedValue( prop: Maybe< CssProp > ): Maybe< string > {
	return 'readonly' === prop?.type ? prop.value : prop?.base?.value;
}

export function hasUserValue( prop: Maybe< CssProp > ): boolean {
	return (
		!! getUserValue( prop ) &&
		getUserValue( prop ) !== getInheritedValue( prop )
	);
}

export function hasInheritedValue( prop: Maybe< CssProp > ): boolean {
	return !! getInheritedValue( prop );
}

export function byName< T extends { readonly name: CssPropName } >(
	props: ReadonlyArray< T >
): Partial< Record< CssPropName, T > > {
	return props.reduce(
		( r, p ) => ( { ...r, [ p.name ]: p } ),
		{} as Partial< Record< CssPropName, T > >
	);
}

// =======
// HELPERS
// =======

const isShorthandProp = ( p: string ) => ! isLeafPropName( p );

const isLeafPropName = ( p: string ): p is CssLeafPropName =>
	( LEAF_PROP_NAMES as string[] ).includes( p );

const fixNumericValues = ( p: CssProp ): CssProp => {
	const value = fixNumericValue( p.value );
	if ( 'readonly' === p.type || ! p.base ) {
		return { ...p, value };
	}

	const baseValue = p.base.value;
	return { ...p, value, base: { ...p.base, value: baseValue } };
};

const fixNumericValue = ( input: string ): string => {
	const [ _, value, units ] = input.match( /^(\d+\.\d{3,})([a-z%]+)$/ ) ?? [];
	if ( ! value || ! units ) {
		return input;
	}

	const newValue = `${ Math.round( parseFloat( value ) ) }`;
	return `${ newValue }${ units }`;
};

// ====
// DATA
// ====

const SHORTHAND_MAP: Record<
	CssShorthandPropName,
	ReadonlyArray< CssShorthandPropName | CssLeafPropName >
> & { _extra: ReadonlyArray< CssLeafPropName > } = {
	background: [
		'backgroundAttachment',
		'backgroundClip',
		'backgroundColor',
		'backgroundImage',
		'backgroundOrigin',
		'backgroundPosition',
		'backgroundRepeat',
		'backgroundSize',
	],
	backgroundPosition: [ 'backgroundPositionX', 'backgroundPositionY' ],
	border: [
		'borderColor',
		'borderStyle',
		'borderWidth',
		'borderRight',
		'borderTop',
		'borderBottom',
		'borderLeft',
	],
	borderColor: [
		'borderTopColor',
		'borderRightColor',
		'borderBottomColor',
		'borderLeftColor',
	],
	borderRadius: [
		'borderTopLeftRadius',
		'borderTopRightRadius',
		'borderBottomRightRadius',
		'borderBottomLeftRadius',
	],
	borderStyle: [
		'borderTopStyle',
		'borderRightStyle',
		'borderBottomStyle',
		'borderLeftStyle',
	],
	borderWidth: [
		'borderTopWidth',
		'borderRightWidth',
		'borderBottomWidth',
		'borderLeftWidth',
	],
	borderTop: [ 'borderTopColor', 'borderTopStyle', 'borderTopWidth' ],
	borderRight: [ 'borderRightColor', 'borderRightStyle', 'borderRightWidth' ],
	borderBottom: [
		'borderBottomColor',
		'borderBottomStyle',
		'borderBottomWidth',
	],
	borderLeft: [ 'borderLeftColor', 'borderLeftStyle', 'borderLeftWidth' ],
	font: [
		'fontStyle',
		'fontVariant',
		'fontWeight',
		'fontSize',
		'lineHeight',
		'fontFamily',
	],
	margin: [ 'marginTop', 'marginRight', 'marginBottom', 'marginLeft' ],
	overflow: [ 'overflowX', 'overflowY' ],
	padding: [ 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft' ],
	textDecoration: [
		'textDecorationLine',
		'textDecorationStyle',
		'textDecorationColor',
		'textDecorationThickness',
	],
	_extra: [
		'alignContent',
		'alignItems',
		'bottom',
		'boxShadow',
		'clear',
		'color',
		'columnCount',
		'columnGap',
		'cursor',
		'display',
		'filter',
		'flexDirection',
		'flexWrap',
		'float',
		'height',
		'justifyContent',
		'left',
		'letterSpacing',
		'minHeight',
		'maxHeight',
		'minWidth',
		'maxWidth',
		'opacity',
		'pointerEvents',
		'position',
		'right',
		'rotate',
		'rowGap',
		'scale',
		'textAlign',
		'textTransform',
		'top',
		'translate',
		'transform',
		'visibility',
		'whiteSpace',
		'width',
		'wordBreak',
		'writingMode',
		'zIndex',
	],
};

const INTERMEDIATE_PROP_NAMES = Object.keys( SHORTHAND_MAP )
	.flatMap( ( n ) => SHORTHAND_MAP[ n as keyof typeof SHORTHAND_MAP ] )
	.filter( ( n ) => n in SHORTHAND_MAP )
	.map( ( n ) => n );
const ROOT_PROP_NAMES = Object.keys( SHORTHAND_MAP )
	.map( ( n ) => n as keyof typeof SHORTHAND_MAP )
	.filter( ( n ) => n !== '_extra' )
	.map( ( n ) => n as CssPropName )
	.filter( ( n ) => ! INTERMEDIATE_PROP_NAMES.includes( n ) );
const LEAF_PROP_NAMES = Object.keys( SHORTHAND_MAP )
	.flatMap( ( n ) => SHORTHAND_MAP[ n as keyof typeof SHORTHAND_MAP ] )
	.filter( ( n ) => ! ( n in SHORTHAND_MAP ) )
	.map( ( n ) => n );

const PROP_NAMES: ReadonlyArray< CssPropName > = [
	...ROOT_PROP_NAMES,
	...INTERMEDIATE_PROP_NAMES,
	...LEAF_PROP_NAMES,
];

function expand( n: CssPropName ): ReadonlyArray< CssLeafPropName > {
	return isLeafPropName( n ) ? [ n ] : SHORTHAND_MAP[ n ].flatMap( expand );
}
const EXPANSIONS = PROP_NAMES.reduce(
	( r, n ) => ( { ...r, [ n ]: uniq( expand( n ) ) } ),
	{} as Record< CssPropName, ReadonlyArray< CssLeafPropName > >
);

const TEST_PROP_VALUES: Record< CssLeafPropName, [ string, string ] > = {
	alignContent: [ 'center', 'space-around' ],
	alignItems: [ 'center', 'flex-end' ],
	backgroundAttachment: [ 'fixed', 'scroll' ],
	backgroundClip: [ 'border-box', 'content-box' ],
	backgroundColor: [ 'red', 'blue' ],
	backgroundImage: [
		'linear-gradient(red,red)',
		'linear-gradient(blue,blue)',
	],
	backgroundOrigin: [ 'border-box', 'content-box' ],
	backgroundPositionX: [ '1px', '2px' ],
	backgroundPositionY: [ '1px', '2px' ],
	backgroundRepeat: [ 'repeat-x', 'repeat-y' ],
	backgroundSize: [ 'cover', 'contain' ],
	borderBottomColor: [ 'red', 'blue' ],
	borderBottomLeftRadius: [ '1px', '2px' ],
	borderBottomRightRadius: [ '1px', '2px' ],
	borderBottomStyle: [ 'dashed', 'dotted' ],
	borderBottomWidth: [ '1px', '2px' ],
	borderLeftColor: [ 'red', 'blue' ],
	borderLeftStyle: [ 'dashed', 'dotted' ],
	borderLeftWidth: [ '1px', '2px' ],
	borderRightColor: [ 'red', 'blue' ],
	borderRightStyle: [ 'dashed', 'dotted' ],
	borderRightWidth: [ '1px', '2px' ],
	borderTopColor: [ 'red', 'blue' ],
	borderTopLeftRadius: [ '1px', '2px' ],
	borderTopRightRadius: [ '1px', '2px' ],
	borderTopStyle: [ 'dashed', 'dotted' ],
	borderTopWidth: [ '1px', '2px' ],
	bottom: [ '1px', '2px' ],
	boxShadow: [ '1px 1px 1px red', '2px 2px 2px blue' ],
	clear: [ 'left', 'right' ],
	color: [ 'red', 'blue' ],
	columnCount: [ '1', '2' ],
	columnGap: [ '1px', '2px' ],
	cursor: [ 'none', 'pointer' ],
	display: [ 'block', 'flex' ],
	filter: [ 'blur(5px)', 'brightness(0.4)' ],
	flexDirection: [ 'row', 'column' ],
	flexWrap: [ 'wrap', 'nowrap' ],
	float: [ 'left', 'right' ],
	fontFamily: [ 'one', 'another' ],
	fontSize: [ '1px', '2px' ],
	fontStyle: [ 'normal', 'italic' ],
	fontVariant: [ 'normal', 'ordinal' ],
	fontWeight: [ '100', '300' ],
	height: [ '1px', '2px' ],
	justifyContent: [ 'normal', 'flex-end' ],
	left: [ '1px', '2px' ],
	letterSpacing: [ '1px', '2px' ],
	lineHeight: [ '1px', '2px' ],
	marginBottom: [ '1px', '2px' ],
	marginLeft: [ '1px', '2px' ],
	marginRight: [ '1px', '2px' ],
	marginTop: [ '1px', '2px' ],
	maxHeight: [ '1px', '2px' ],
	maxWidth: [ '1px', '2px' ],
	minHeight: [ '1px', '2px' ],
	minWidth: [ '1px', '2px' ],
	opacity: [ '0.5', '0.8' ],
	overflowX: [ 'hidden', 'scroll' ],
	overflowY: [ 'hidden', 'scroll' ],
	paddingBottom: [ '1px', '2px' ],
	paddingLeft: [ '1px', '2px' ],
	paddingRight: [ '1px', '2px' ],
	paddingTop: [ '1px', '2px' ],
	pointerEvents: [ 'auto', 'none' ],
	position: [ 'static', 'relative' ],
	right: [ '1px', '2px' ],
	rotate: [ '90deg', 'y 180deg' ],
	rowGap: [ '1px', '2px' ],
	scale: [ '1.5', '1.7 50%' ],
	textAlign: [ 'left', 'right' ],
	textDecorationColor: [ 'red', 'blue' ],
	textDecorationLine: [ 'underline', 'overline' ],
	textDecorationStyle: [ 'dotted', 'dashed' ],
	textDecorationThickness: [ '1px', '2px' ],
	textTransform: [ 'lowercase', 'uppercase' ],
	top: [ '1px', '2px' ],
	translate: [ '40px', '50% -40%' ],
	transform: [ 'translateX(10px)', 'translateX(20px)' ],
	visibility: [ 'visible', 'hidden' ],
	whiteSpace: [ 'normal', 'pre' ],
	width: [ '1px', '2px' ],
	wordBreak: [ 'normal', 'break-word' ],
	writingMode: [ 'horizontal-tb', 'vertical-rl' ],
	zIndex: [ '1', '2' ],
};

const strintifyTestProps = ( i: 0 | 1, imp: boolean ) =>
	Object.keys( TEST_PROP_VALUES )
		.map( ( k ) => k as CssLeafPropName )
		.map( ( k ) => [ kebabCase( k ), TEST_PROP_VALUES[ k ][ i ] ] as const )
		.map( ( [ k, v ] ) => `${ k }: ${ v } ${ imp ? '!important' : '' };` )
		.join( '\n' );

const TEST_PROPS_CSS = {
	base: strintifyTestProps( 0, false ),
	importantBase: strintifyTestProps( 0, true ),
	alternative: strintifyTestProps( 1, false ),
	importantAlternative: strintifyTestProps( 1, true ),
};
